import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import {
    RuntimeMode,
    useCreateTopologyMutation,
    useLazyCheckIfTopologyExistQuery
} from '../../app/apiSlice';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './topologiesForm.scss';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks/hooks';
import { SelectMode } from '../../components/Mode/SelectMode';
import { InputTextarea } from 'primereact/inputtextarea';
import { NewTopologyTitle } from './NewTopologyTitle';
import { setToast } from '../../app/appSlice';
import { DropdownItem } from '../workflows/Workflow';
import { Dropdown } from 'primereact/dropdown';
import { envTemplate } from '../../components/environnement/envTemplate';
import { LabelsSelectors, useLabelsSelectors } from '@kosmos/shared';

export type FormFields = {
    [key: string]: any;
}

export type LabelFields = {
    [key: string]: DropdownItem | DropdownItem[];
}

type FormDataTopology = {
    name: string;
    description: string;
    environment: DropdownItem;
    mode: string;
    labels: LabelFields;
}

export const NewTopologyForm = ({onClose}: { onClose: () => void }) => {
    const runtimes = useAppSelector((state) => state.app.config.runtimeMode);
    const environnements = useAppSelector((state) => state.app.config.environnements);
    const { selectedLabels, validate: validateLabels } = useLabelsSelectors();

    const {
        formState: {errors},
        handleSubmit,
        reset,
        control
    } = useForm<FormFields>({
        mode: 'onSubmit', reValidateMode: 'onChange', defaultValues: {
            name: '', description: '', environment: environnements && environnements.length > 0 ? environnements[0] : null
        },
    });
    const {t} = useTranslation();

    const [createTopology, {...status}] = useCreateTopologyMutation();
    const [checkIfTopologyExist, {data: topologyExist}] = useLazyCheckIfTopologyExistQuery();

    const history = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (status.isSuccess) {
            reset();
            onClose();
        }
    }, [status.isSuccess]);

    const onSubmit = async (data: FormFields) => {
        const formattedData = data as unknown as FormDataTopology;
        if (!validateLabels()) {
            return;
        }

        try {
            const topologyExist = await checkIfTopologyExist({
                name: formattedData.name,
            }).unwrap();
            if (!topologyExist.topologyExist) {
                try {
                    const topo = await createTopology({
                        name: formattedData.name,
                        description: formattedData.description,
                        environnement: formattedData.environment.code,
                        metadatas: selectedLabels,
                        runtimeMode: formattedData.mode,
                        type: 'new',
                    }).unwrap();
                    if (topo.id) {
                        history(`/treatments/editor?t=${topo.id}&v=${topo.version}`);
                    }
                } catch {
                    dispatch(
                        setToast({
                            severity: 'error',
                            title: 'Erreur',
                            content: `Echec de la création du traitement ${formattedData.name}`,
                            life: 3000,
                        })
                    );
                }
            } else {
                const errorMsg = topologyExist.topologyExist ? `Le nom ${formattedData.name} existe déjà` : 'Modes d\'exécution manquants dans la configuration';
                dispatch(
                    setToast({
                        severity: 'error',
                        title: `Erreur`,
                        content: errorMsg,
                        life: 3000,
                    })
                );
            }
        } catch {
            dispatch(
                setToast({
                    severity: 'error',
                    title: 'Erreur',
                    content: `Echec de la création du traitement ${formattedData.name}`,
                    life: 3000,
                })
            );
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} className="topologyForm p-fluid">
                <div className="h-full flex flex-column">
                    <div className="flex-grow-1">
                        <NewTopologyTitle control={control} errors={errors}/>

                        <div className="p-field mt-4">
                            <span className="p-label">
                                <label
                                    htmlFor="description"
                                    className={classNames({'p-error': errors.description})}
                                    style={{fontWeight: 'bold'}}
                                >
                                    {t('label.description')}
                                </label>
                                <Controller
                                    name={'description'}
                                    control={control}
                                    render={({field}) => (
                                        <InputTextarea id={field.name} {...field} maxLength={128} />
                                    )}
                                />
                            </span>
                        </div>

                        <div className="p-field mt-4">
                            <span className="p-label">
                                <label
                                    htmlFor="environment"
                                    className={classNames({'p-error': errors.environment})}
                                    style={{fontWeight: 'bold'}}
                                >
                                    {t('label.environment')}*</label>
                                <Controller
                                    name="environment"
                                    control={control}
                                    rules={{
                                        required: `${t('label.environment')} ${t('label.required-m')}.`,
                                    }}
                                    render={({field}) => (
                                        <Dropdown
                                            id={field.name} value={field.value}
                                            options={environnements}
                                            onChange={(e) => field.onChange(e.value)}
                                            optionLabel="name"
                                            placeholder="Selectionner un environnement"
                                            className="w-full"
                                            itemTemplate={envTemplate}
                                            valueTemplate={envTemplate}
                                        />
                                    )}
                                />
                            </span>
                        </div>

                        <LabelsSelectors />

                        <div className="p-field mt-4">
                            <span className="p-label">
                                <label
                                    className={classNames({'p-error': errors.description})}
                                    style={{fontWeight: 'bold'}}
                                >
                                Mode
                                </label>
                                <div className="mb-2">Attention, une fois le traitement crée, le mode ne pourra plus être modifié</div>
                                    {runtimes.map((runtime) => (
                                        <div className="mb-4" key={runtime.name}>
                                            <Controller
                                                name={'mode'}
                                                control={control}
                                                defaultValue={RuntimeMode.ANALYTIC.toString()}
                                                render={({field}) => (
                                                  <SelectMode field={field} runtime={runtime}/>
                                                )}
                                            />
                                        </div>
                                    ))}
                            </span>
                        </div>
                    </div>

                    <div className="flex mb-2 py-3">
                        <div className="flex-grow-1 mr-1">
                            <Button
                                label={t('button.cancel')}
                                onClick={() => {
                                    onClose();
                                }}
                                className="p-button-text"
                            />
                        </div>
                        <div className="flex-grow-1">
                            <Button
                                type="submit"
                                loading={status.isLoading}
                                label={t('button.submit')}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};
